/**
 * FC.02 — InventarioReferenciaBuilder
 *
 * Construye el inventario de referencia oficial para un municipio consultando
 * los servicios R2 ya operativos. Normaliza respuestas heterogéneas a
 * CandidatoReferencia[] uniforme con cache LRU por municipio.
 *
 * @module services/cotejo/InventarioReferenciaBuilder
 * @version 1.0.0
 */

import type { CandidatoReferencia, FuenteReferencia } from './types';
import { InfrastructureType } from '../../types/infrastructure';
import type { DERAR2Feature } from '../../types/deraR2';
import type { DERAG10Feature } from '../../types/deraG10';
import type { DERAG3Feature } from '../../types/deraG3';
import type { IAPHFeature } from '../../types/iaph';
import type { EduCenterFeature } from '../../types/eduCenters';
import type { GCFacilityFeature } from '../../types/gcFacilities';
import type { OSMSecurityFeature } from '../../types/osmSecurity';

// Servicios R2 reales (para factory createDefault)
import { DERAR2Service } from '../dera/DERAR2Service';
import { DERAG10R2Service } from '../geocoding/specialized/DERAG10R2Service';
import { DERAG3R2Service } from '../geocoding/specialized/DERAG3R2Service';
import { IAPHR2Service } from '../geocoding/specialized/IAPHR2Service';
import { EduCentersR2Service } from '../geocoding/specialized/EduCentersR2Service';
import { GCFacilitiesR2Service } from '../geocoding/specialized/GCFacilitiesR2Service';
import { OSMSecurityR2Service } from '../geocoding/specialized/OSMSecurityR2Service';
import { AGOLDataService } from '../data/AGOLDataService';

// =============================================================================
// INTERFACES PARA INYECCIÓN DE DEPENDENCIAS
// =============================================================================

/** Contrato mínimo que un servicio R2 debe cumplir */
export interface R2MunicipioLoader<T> {
  loadMunicipio(codINE: string): Promise<T[]>;
}

/** Contrato para AGOL (API diferente) */
export interface AGOLLoader {
  getFeatures(codINE: string): Promise<Array<{
    properties: { nombre: string; cod_mun: string; agol_categoria: string; direccion?: string | null };
    geometry: { type: string; coordinates: number[] | number[][] | number[][][] | number[][][][] };
  }>>;
}

/** Dependencias R2 inyectables (para testing) */
export interface R2ServiceDeps {
  deraG12: R2MunicipioLoader<DERAR2Feature>;
  deraG10: R2MunicipioLoader<DERAG10Feature>;
  deraG3: R2MunicipioLoader<DERAG3Feature>;
  iaph: R2MunicipioLoader<IAPHFeature>;
  eduCenters: R2MunicipioLoader<EduCenterFeature>;
  gcFacilities: R2MunicipioLoader<GCFacilityFeature>;
  osmSecurity: R2MunicipioLoader<OSMSecurityFeature>;
  agol: AGOLLoader;
}

// =============================================================================
// MAPEO ESTÁTICO: InfrastructureType → DERATipologia para filtro DERA G12
// =============================================================================

const DERA_G12_FILTER: Partial<Record<InfrastructureType, string>> = {
  [InfrastructureType.HEALTH]: 'SANITARIO',
  [InfrastructureType.EDUCATION]: 'EDUCATIVO',
  [InfrastructureType.POLICE]: 'SEGURIDAD',
  [InfrastructureType.FIRE]: 'SEGURIDAD',
  [InfrastructureType.CULTURAL]: 'CULTURAL',
  [InfrastructureType.SPORTS]: 'DEPORTIVO',
  [InfrastructureType.MUNICIPAL]: 'ADMINISTRATIVO',
  [InfrastructureType.SOCIAL]: 'SOCIAL',
  [InfrastructureType.FUEL]: 'COMBUSTIBLE',
  [InfrastructureType.TELECOM]: 'COMUNICACIONES',
};

/**
 * Subtipos DERA G12 por tipología PTEL cuando varias tipologías comparten capa.
 *
 * `IDREP` (24-oct-2026): la capa `SEGURIDAD` agrupa policía, Guardia Civil, bomberos
 * y gestión de emergencias. Filtrar solo por `SEGURIDAD` metía cada registro bajo
 * `POLICE` y bajo `FIRE`: la Policía Local salía también como bomberos y viceversa,
 * con el mismo `idFuente` repetido. Medido en Colomera (2 de 2 registros) y Guadix
 * (3 de 3); en toda Andalucía, 619 de 785 municipios tienen algún registro de
 * seguridad. El prefijo de capa del id (`g12_26_Policia`, `g12_29_ParqueBomberos`,
 * `g12_34_GuardiaCivil`) y el `subtipo` coinciden en los 1 280 registros.
 *
 * `EMERGENCIAS` (`g12_35_GestionEmergencias`, 23 registros en 12 municipios) no
 * tiene tipología de destino: queda fuera del inventario (`DOP-5`=(a), pendiente
 * `OBS-COTEJO-LOS-EMERGENCIAS-DE-DERA-G12-NO-TIENEN-TIPOLOGIA-EN-EL-INVENTARIO`).
 */
const DERA_G12_SUBTIPOS_BY_TIPO: Partial<Record<InfrastructureType, string[]>> = {
  [InfrastructureType.POLICE]: ['POLICIA', 'GUARDIA_CIVIL'],
  [InfrastructureType.FIRE]: ['BOMBEROS'],
};

/**
 * Tabla de fuentes R2 por tipología.
 * Cada entrada indica qué servicios consultar para esa tipología.
 */
const SOURCES_BY_TIPO: Partial<Record<InfrastructureType, FuenteReferencia[]>> = {
  [InfrastructureType.HEALTH]: ['DERA_G12'],
  [InfrastructureType.EDUCATION]: ['DERA_G12', 'EduCenters'],
  [InfrastructureType.POLICE]: ['DERA_G12', 'GCFacilities', 'OSMSecurity'],
  [InfrastructureType.FIRE]: ['DERA_G12'],
  [InfrastructureType.HYDRAULIC]: ['DERA_G3', 'DERA_G10'],
  [InfrastructureType.ENERGY]: ['DERA_G10'],
  [InfrastructureType.SPORTS]: ['DERA_G12'],
  [InfrastructureType.CULTURAL]: ['DERA_G12', 'IAPH'],
  [InfrastructureType.RSU]: ['DERA_G10'],
  [InfrastructureType.MUNICIPAL]: ['DERA_G12'],
  [InfrastructureType.SOCIAL]: ['DERA_G12'],
  [InfrastructureType.INDUSTRIAL]: ['DERA_G10'],
  // `IDREP` (24-oct-2026): `RELIGIOUS` pedía `IAPH` igual que `CULTURAL`, y
  // `normalizeIAPH` etiqueta todo como `CULTURAL` (el `tipo` del IAPH vale
  // «Inmueble» en los 41 bienes medidos de Colomera y Guadix): cada bien entraba
  // dos veces con el mismo `idFuente`. Sin entrada `RELIGIOUS` no se pierde ningún
  // bien; la tipología del candidato no cambia.
};

/**
 * Filtro de subtipos G10 por tipología PTEL.
 * Evita que features EDAR aparezcan al consultar ENERGY, etc.
 */
const G10_SUBTIPOS_BY_TIPO: Partial<Record<InfrastructureType, string[]>> = {
  [InfrastructureType.ENERGY]: [
    'CENTRAL_HIDROELECTRICA', 'PARQUE_EOLICO', 'PLANTA_SOLAR',
    'BIOMASA', 'BIOGAS', 'COGENERACION', 'TERMICA', 'SUBESTACION',
  ],
  [InfrastructureType.HYDRAULIC]: ['ETAP'],
  [InfrastructureType.RSU]: ['RSU', 'PUNTO_LIMPIO'],
  [InfrastructureType.INDUSTRIAL]: ['EDAR'], // EDAR industrial
};

// =============================================================================
// NORMALIZADORES: Feature heterogéneo → CandidatoReferencia uniforme
// =============================================================================

function normalizeDERAG12(f: DERAR2Feature, tipo: InfrastructureType): CandidatoReferencia {
  return {
    idFuente: f.id, nombre: f.nombre, tipologia: tipo, fuente: 'DERA_G12',
    x25830: f.x, y25830: f.y, ine5: f.cod_ine, direccion: f.direccion,
  };
}

function normalizeDERAG10(f: DERAG10Feature, tipo: InfrastructureType): CandidatoReferencia {
  return {
    idFuente: f.id, nombre: f.nombre, tipologia: tipo, fuente: 'DERA_G10',
    x25830: f.x, y25830: f.y, ine5: f.cod_ine, direccion: undefined,
    metadata: f.meta?.potencia_kw ? { potencia: Number(f.meta.potencia_kw), potenciaUnidad: 'kW' } : undefined,
  };
}

function normalizeDERAG3(f: DERAG3Feature): CandidatoReferencia {
  return {
    idFuente: f.id, nombre: f.nombre, tipologia: InfrastructureType.HYDRAULIC, fuente: 'DERA_G3',
    x25830: f.x, y25830: f.y, ine5: f.cod_ine, direccion: undefined,
    metadata: f.meta?.capacidad_m3 ? { capacidadM3: Number(f.meta.capacidad_m3) } : undefined,
  };
}

function normalizeIAPH(f: IAPHFeature): CandidatoReferencia {
  return {
    idFuente: `iaph-${f.codigo}`, nombre: f.nombre, fuente: 'IAPH',
    tipologia: InfrastructureType.CULTURAL, x25830: f.x, y25830: f.y,
    ine5: f.cod_ine, metadata: f.protegido ? { esBIC: true } : undefined,
  };
}

function normalizeEduCenter(f: EduCenterFeature): CandidatoReferencia {
  return {
    idFuente: f.id, nombre: f.nombre, tipologia: InfrastructureType.EDUCATION,
    fuente: 'EduCenters', x25830: f.x, y25830: f.y, ine5: f.codMun,
    direccion: f.direccion,
  };
}

function normalizeGCFacility(f: GCFacilityFeature): CandidatoReferencia {
  return {
    idFuente: f.id, nombre: f.nombre, tipologia: InfrastructureType.POLICE,
    fuente: 'GCFacilities', x25830: f.x, y25830: f.y, ine5: f.codMun,
    direccion: f.direccion,
  };
}

function normalizeOSMSecurity(f: OSMSecurityFeature): CandidatoReferencia {
  return {
    idFuente: f.id, nombre: f.nombre, tipologia: InfrastructureType.POLICE,
    fuente: 'OSMSecurity', x25830: f.x, y25830: f.y, ine5: f.codMun,
    direccion: f.direccion,
  };
}

// =============================================================================
// CONSTANTES
// =============================================================================

const CACHE_MAX = 20;

// =============================================================================
// CLASE PRINCIPAL
// =============================================================================

interface CacheEntry {
  data: Map<InfrastructureType, CandidatoReferencia[]>;
  timestamp: number;
}

export class InventarioReferenciaBuilder {
  private readonly deps: R2ServiceDeps;
  private readonly cache = new Map<string, CacheEntry>();
  private readonly cacheTTL = 30 * 60 * 1000; // 30 min

  constructor(deps: R2ServiceDeps) {
    this.deps = deps;
  }

  /**
   * Factory que instancia el builder con los servicios R2 reales de producción.
   * Uso: `InventarioReferenciaBuilder.createDefault()`
   */
  static createDefault(): InventarioReferenciaBuilder {
    const agolService = new AGOLDataService();
    return new InventarioReferenciaBuilder({
      deraG12: new DERAR2Service(),
      deraG10: new DERAG10R2Service(),
      deraG3: new DERAG3R2Service(),
      iaph: new IAPHR2Service(),
      eduCenters: new EduCentersR2Service(),
      gcFacilities: new GCFacilitiesR2Service(),
      osmSecurity: new OSMSecurityR2Service(),
      agol: { getFeatures: (codINE: string) => agolService.getFeatures(codINE) } as AGOLLoader,
    });
  }

  /**
   * Construye inventario de referencia para un municipio.
   * @param ine5 — Código INE 5 dígitos
   * @param tipologias — Tipologías a consultar (vacío = todas con fuentes)
   * @returns Mapa tipología → candidatos normalizados
   */
  async build(
    ine5: string,
    tipologias: InfrastructureType[] = [],
  ): Promise<Map<InfrastructureType, CandidatoReferencia[]>> {
    // Tipologías efectivas: las solicitadas o todas las que tienen fuentes
    const tiposEfectivos = tipologias.length > 0
      ? tipologias.filter(t => SOURCES_BY_TIPO[t])
      : Object.keys(SOURCES_BY_TIPO) as InfrastructureType[];

    // Cache check
    const cacheKey = `${ine5}:${[...tiposEfectivos].sort().join(',')}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    // Construir inventario en paralelo por tipología
    const result = new Map<InfrastructureType, CandidatoReferencia[]>();
    const promises = tiposEfectivos.map(async (tipo) => {
      const candidatos = await this.loadForTipologia(ine5, tipo);
      if (candidatos.length > 0) {
        result.set(tipo, candidatos);
      }
    });

    await Promise.all(promises);

    this.setInCache(cacheKey, result);
    return result;
  }

  /** Carga y normaliza candidatos de todas las fuentes para una tipología */
  private async loadForTipologia(
    ine5: string,
    tipo: InfrastructureType,
  ): Promise<CandidatoReferencia[]> {
    const fuentes = SOURCES_BY_TIPO[tipo] ?? [];
    const results: CandidatoReferencia[] = [];

    for (const fuente of fuentes) {
      try {
        const candidatos = await this.loadFromSource(ine5, tipo, fuente);
        results.push(...candidatos);
      } catch (err) {
        console.warn(`[InventarioRef] Error cargando ${fuente} para ${ine5}/${tipo}:`, err);
      }
    }

    return results;
  }

  /** Carga de una fuente R2 concreta, filtra y normaliza */
  private async loadFromSource(
    ine5: string,
    tipo: InfrastructureType,
    fuente: FuenteReferencia,
  ): Promise<CandidatoReferencia[]> {
    switch (fuente) {
      case 'DERA_G12': {
        const all = await this.deps.deraG12.loadMunicipio(ine5);
        const filterTip = DERA_G12_FILTER[tipo];
        const porTipologia = filterTip ? all.filter(f => f.tipologia === filterTip) : all;
        const subtipos = DERA_G12_SUBTIPOS_BY_TIPO[tipo];
        const filtered = subtipos
          ? porTipologia.filter(f => subtipos.includes(f.subtipo))
          : porTipologia;
        return filtered.map(f => normalizeDERAG12(f, tipo));
      }
      case 'DERA_G10': {
        const all = await this.deps.deraG10.loadMunicipio(ine5);
        const subtipos = G10_SUBTIPOS_BY_TIPO[tipo];
        const filtered = subtipos
          ? all.filter(f => subtipos.includes(f.subtipo))
          : all;
        return filtered.map(f => normalizeDERAG10(f, tipo));
      }
      case 'DERA_G3': {
        const all = await this.deps.deraG3.loadMunicipio(ine5);
        return all.map(f => normalizeDERAG3(f));
      }
      case 'IAPH': {
        const all = await this.deps.iaph.loadMunicipio(ine5);
        return all.map(f => normalizeIAPH(f));
      }
      case 'EduCenters': {
        const all = await this.deps.eduCenters.loadMunicipio(ine5);
        return all.map(f => normalizeEduCenter(f));
      }
      case 'GCFacilities': {
        const all = await this.deps.gcFacilities.loadMunicipio(ine5);
        return all.map(f => normalizeGCFacility(f));
      }
      case 'OSMSecurity': {
        const all = await this.deps.osmSecurity.loadMunicipio(ine5);
        return all.map(f => normalizeOSMSecurity(f));
      }
      default:
        return [];
    }
  }

  // Cache LRU ---------------------------------------------------------------

  private getFromCache(key: string): Map<InfrastructureType, CandidatoReferencia[]> | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > this.cacheTTL) {
      this.cache.delete(key);
      return null;
    }
    return entry.data;
  }

  private setInCache(key: string, data: Map<InfrastructureType, CandidatoReferencia[]>): void {
    if (this.cache.size >= CACHE_MAX) {
      const oldest = this.cache.keys().next().value;
      if (oldest) this.cache.delete(oldest);
    }
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  /** Limpia cache (útil en tests) */
  clearCache(): void {
    this.cache.clear();
  }

  /** Tipologías que tienen fuentes de referencia configuradas */
  static getTipologiasDisponibles(): InfrastructureType[] {
    return Object.keys(SOURCES_BY_TIPO) as InfrastructureType[];
  }

  /** Fuentes R2 configuradas para una tipología */
  static getFuentesParaTipologia(tipo: InfrastructureType): FuenteReferencia[] {
    return SOURCES_BY_TIPO[tipo] ?? [];
  }
}
